import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Landmark } from "lucide-react";

/*
  Premium 7-slide cinematic hero for Adiyogi Handicrafts.
  - Embla for swipe / drag on mobile
  - Auto-advance every 6s (pauses on hover / when tab is hidden)
  - Manual dot + arrow navigation
  - Ken Burns slow zoom on every slide image for a "cinematic" feel
  - Overlay copy is exactly as requested by the brand
*/

const SLIDES = [
  {
    kicker: "The Spirit of Adiyogi",
    title: "The Spirit of Adiyogi",
    subtitle: "Bring divine grace and timeless spirituality into your home.",
    cta: { label: "Shop Premium Pooja Collection", to: "/shop?category=pooja-items" },
    image: "/adiyogi-hero.jpg",
    tone: "dark",
    align: "left",
  },
  {
    kicker: "Traditional Craftsmanship",
    title: "Traditional Craftsmanship",
    subtitle: "Beautifully designed pooja essentials for sacred moments.",
    cta: { label: "Shop Pooja Collection", to: "/shop?category=pooja-items" },
    image: "/products/thali-1.jpg",
    tone: "dark",
    align: "left",
  },
  {
    kicker: "Light Up Your Sacred Space",
    title: "Light Up Your Sacred Space",
    subtitle: "Traditional elegance crafted for every auspicious occasion.",
    cta: { label: "Shop Deepams", to: "/product/peacock-villaku-german-silver-heavy-pair" },
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/73blf55p_IMG_20260819_224404%5B1%5D.jpg",
    tone: "dark",
    align: "right",
  },
  {
    kicker: "Divine Beauty for Every Pooja",
    title: "Divine Beauty for Every Pooja",
    subtitle: "Complete your sacred celebrations with timeless traditional pieces.",
    cta: { label: "Explore Varalakshmi Collection", to: "/product/varalakshmi-pooja-german-silver-complete-combo-4kg" },
    image:
      "https://customer-assets-4nw71qhi.emergentagent.net/job_adiyogi-shop/artifacts/2ki4p1ay_Image-89024%5B1%5D.jpg",
    tone: "dark",
    align: "left",
  },
  {
    kicker: "Auspicious Beginnings",
    title: "Gifts Rooted in Tradition",
    subtitle: "Thoughtful handcrafted gifts for every special occasion.",
    cta: { label: "Shop Gifts", to: "/shop?category=gift-collection" },
    image: "/products/lakshmi-ganesh-1.jpg",
    tone: "dark",
    align: "right",
  },
  {
    kicker: "Tradition · Craftsmanship · Heritage",
    title: "A Karnataka Government Initiative",
    subtitle:
      "Celebrating traditional craftsmanship and spiritual heritage through beautifully curated handicrafts.",
    cta: { label: "Discover Our Story", to: "/about" },
    image: "/adiyogi-hero.jpg",
    tone: "dark",
    align: "center",
    badge: true,
  },
  {
    kicker: "Adiyogi Handicrafts",
    title: "Timeless Tradition. Premium Craftsmanship.",
    subtitle: "Discover our curated collection of spiritual and traditional handicrafts.",
    cta: { label: "Shop All Products", to: "/shop" },
    image: "/products/thali-2.jpg",
    tone: "dark",
    align: "left",
  },
];

export default function HeroSlider() {
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, align: "start", dragFree: false });
  const [selected, setSelected] = useState(0);
  const [paused, setPaused] = useState(false);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    onSelect();
  }, [embla, onSelect]);

  // Auto-advance every 6s, pause on hover / when tab hidden
  useEffect(() => {
    if (!embla || paused) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") embla.scrollNext();
    }, 6000);
    return () => clearInterval(t);
  }, [embla, paused]);

  const scrollTo = (i) => embla && embla.scrollTo(i);

  return (
    <section
      className="relative"
      data-testid="hero-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="overflow-hidden" ref={emblaRef}>
        <div className="flex">
          {SLIDES.map((s, i) => (
            <div
              key={s.title}
              className="relative min-w-0 flex-[0_0_100%] h-[68vh] min-h-[460px] md:h-[78vh] md:min-h-[640px]"
              data-testid={`hero-slide-${i}`}
            >
              {/* Ken-Burns background image */}
              <div
                className={`absolute inset-0 bg-center bg-cover kenburns-${(i % 2) + 1}`}
                style={{ backgroundImage: `url(${s.image})` }}
              />
              {/* Cinematic gradient */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              {/* Soft golden edge glow */}
              <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 220px rgba(184,134,11,0.18)" }} />

              {/* Copy */}
              <div
                className={`relative container-x h-full flex ${
                  s.align === "right" ? "justify-end" : s.align === "center" ? "justify-center" : "justify-start"
                } items-center`}
              >
                <div
                  className={`max-w-2xl text-ivory ${
                    s.align === "center" ? "text-center" : ""
                  } fade-in-slow`}
                >
                  <div className="flex items-center gap-2 mb-4 text-[10px] md:text-xs uppercase tracking-[0.4em] text-[#E8C878]">
                    {s.badge && <Landmark className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    {s.kicker}
                  </div>
                  <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight drop-shadow-lg">
                    {s.title.split(".").map((line, j, arr) => (
                      <span key={j} className={j === arr.length - 1 && arr.length > 1 ? "gold-text italic block" : "block"}>
                        {line}
                        {j < arr.length - 1 ? "." : ""}
                      </span>
                    ))}
                  </h1>
                  <p className="mt-5 md:mt-6 text-sm md:text-lg text-ivory/85 max-w-xl">
                    “{s.subtitle}”
                  </p>
                  <div className={`mt-7 md:mt-9 flex flex-wrap gap-3 ${s.align === "center" ? "justify-center" : ""}`}>
                    <Link
                      to={s.cta.to}
                      className="btn-gold rounded-full px-7 md:px-8 py-3 text-[11px] md:text-xs uppercase tracking-widest"
                      data-testid={`hero-cta-${i}`}
                    >
                      {s.cta.label}
                    </Link>
                    <a
                      href="#home-best-sellers"
                      className="rounded-full px-7 md:px-8 py-3 text-[11px] md:text-xs uppercase tracking-widest border border-[#E8C878]/70 text-ivory hover:bg-white/10 transition"
                      data-testid={`hero-secondary-cta-${i}`}
                    >
                      Explore Best Sellers
                    </a>
                  </div>
                  <div className={`mt-5 text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-[#F5DFA6] ${s.align === "center" ? "text-center" : ""}`} data-testid={`hero-trust-line-${i}`}>
                    Handcrafted • Premium Finish • All India &amp; Worldwide Shipping
                  </div>
                  {s.badge && (
                    <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/10 border border-[#E8C878]/40 backdrop-blur-sm px-3 py-1 text-[10px] md:text-xs tracking-[0.25em] uppercase text-[#F5DFA6]">
                      Government of Karnataka
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={() => embla && embla.scrollPrev()}
        aria-label="Previous slide"
        data-testid="hero-prev"
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center bg-black/30 hover:bg-black/50 text-ivory backdrop-blur-sm border border-white/20 transition"
      >
        <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
      </button>
      <button
        onClick={() => embla && embla.scrollNext()}
        aria-label="Next slide"
        data-testid="hero-next"
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full items-center justify-center bg-black/30 hover:bg-black/50 text-ivory backdrop-blur-sm border border-white/20 transition"
      >
        <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
      </button>

      {/* Dots */}
      <div className="absolute bottom-5 md:bottom-8 left-0 right-0 flex justify-center gap-2" data-testid="hero-dots">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => scrollTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            data-testid={`hero-dot-${i}`}
            className={`h-1.5 rounded-full transition-all ${
              i === selected ? "bg-[#E8C878] w-9" : "bg-white/40 w-4 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
